import React, { useEffect, useRef, useState } from 'react';
import Spinner from '../../shared/Spinner';
import Paginator from '../../shared/Paginator';
import { useNavigate } from 'react-router-dom';
import { Form, Dropdown } from 'react-bootstrap';
import { Clipboard } from 'react-bootstrap-icons';
import useApi from '../../hooks/UseApi';
import useAuthentication from '../../auth/useAuthentication';
import moment from 'moment';

const Rows = ({ data, onRowClick, onCopy }) => (
  <>
    {data.map((item) => (
      <tr key={item.id} onClick={() => onRowClick(item)} style={{ cursor: 'pointer' }}>
        <td className="text-nowrap">{moment(item.date).format('DD/MM/YYYY HH:mm:ss')}</td>
        <td className="text-nowrap">{item.machine}</td>
        <td className="text-nowrap">{item.type}</td>
        <td className="text-truncate" style={{ maxWidth: '250px' }}>{item.content_received}</td>
        <td className="text-truncate text-muted" style={{ maxWidth: '200px', fontSize: '0.8rem' }}>{item.url_sent}</td>
        <td className="text-truncate" style={{ maxWidth: '250px' }}>{item.content_sent}</td>
        <td>
          <a href="#!" title="Copiar recibido" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCopy(item.content_received); }}>
            <Clipboard />
          </a>
        </td>
      </tr>
    ))}
  </>
);

// Persistimos los filtros para no perderlos al entrar a un detalle y volver
const STORAGE_KEY = 'logs-filters';
const loadFilters = () => {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
};

const Logs = () => {
  const startOfToday                = moment().startOf('day').format('YYYY-MM-DDTHH:mm');
  const endOfToday                  = moment().endOf('day').format('YYYY-MM-DDTHH:mm');
  const saved                       = loadFilters();
  const [fromDate, setFromDate]     = useState(saved.fromDate ?? startOfToday);
  const [toDate, setToDate]         = useState(saved.toDate ?? endOfToday);
  const [received, setReceived]     = useState(saved.received ?? '');
  const [sent, setSent]             = useState(saved.sent ?? '');
  const [receivedQuery, setReceivedQuery] = useState(saved.received ?? '');
  const [sentQuery, setSentQuery]   = useState(saved.sent ?? '');
  const [data, setData]             = useState();
  const [page, setPage]             = useState(saved.page ?? 1);
  const [total, setTotal]           = useState(0);
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(saved.selectedTypes ?? []);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { get, updating }           = useApi();
  const navigate                    = useNavigate();
  const context                     = useAuthentication();

  // Guardamos los filtros vigentes en sessionStorage en cada cambio
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      fromDate, toDate, received, sent, selectedTypes, page,
    }));
  }, [fromDate, toDate, received, sent, selectedTypes, page]);

  // Debounce de los filtros de texto: re-consultamos 400ms después de dejar de tipear
  const firstReceived = useRef(true);
  useEffect(() => {
    if (firstReceived.current) { firstReceived.current = false; return; }
    const t = setTimeout(() => { setPage(1); setReceivedQuery(received); }, 400);
    return () => clearTimeout(t);
  }, [received]);

  const firstSent = useRef(true);
  useEffect(() => {
    if (firstSent.current) { firstSent.current = false; return; }
    const t = setTimeout(() => { setPage(1); setSentQuery(sent); }, 400);
    return () => clearTimeout(t);
  }, [sent]);

  // silent = refresco en background (sin Spinner), usado por el auto-refresh
  // toOverride = "Hasta" puntual (el auto-refresh usa la hora actual recién calculada)
  const getData = (silent = false, toOverride) => {
    const machineId = context.user?.selectedMachine?.id;
    const to = toOverride ?? toDate;
    let path = `v2/logs?from=${fromDate}&to=${to}`;
    if (machineId) path += `&machine=${machineId}`;
    if (selectedTypes.length) path += `&type=${selectedTypes.join(',')}`;
    if (receivedQuery) path += `&received=${encodeURIComponent(receivedQuery)}`;
    if (sentQuery)     path += `&sent=${encodeURIComponent(sentQuery)}`;
    get(path, { page, skipUpdating: silent }).then(r => {
      setTotal(parseInt(r.headers['x-paging-total-pages']) || 0);
      setData(r.data);
    });
  };

  // Con auto-refresh encendido la consulta es silenciosa (sin Spinner)
  useEffect(() => { getData(autoRefresh); }, [fromDate, toDate, page, selectedTypes, receivedQuery, sentQuery, autoRefresh, context.user?.selectedMachine?.id]);

  // Auto-refresh tipo "tail -f": cada 5s fija "Hasta" a la hora actual y re-consulta manteniendo los filtros
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      const now = moment().format('YYYY-MM-DDTHH:mm');
      setToDate(now);
      getData(true, now);
    }, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fromDate, selectedTypes, receivedQuery, sentQuery, page, context.user?.selectedMachine?.id]);

  useEffect(() => {
    get('v2/logs/types').then(r => setTypeOptions(r.data || []));
  }, []);

  const handleDateChange = (setter) => (e) => {
    setPage(1);
    setter(e.target.value);
  };

  const handleTypeToggle = (type) => {
    setPage(1);
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
  };

  // Limpia todos los filtros y vuelve a los valores por defecto
  const handleClearAll = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setFromDate(startOfToday);
    setToDate(endOfToday);
    setReceived('');
    setSent('');
    setReceivedQuery('');
    setSentQuery('');
    setSelectedTypes([]);
    setAutoRefresh(false);
    setPage(1);
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="row align-items-end mb-3">
          <div className="col-auto">
            <Form.Group>
              <Form.Label>Desde</Form.Label>
              <Form.Control type="datetime-local" value={fromDate} onChange={handleDateChange(setFromDate)} />
            </Form.Group>
          </div>
          <div className="col-auto">
            <Form.Group>
              <Form.Label>Hasta</Form.Label>
              <Form.Control type="datetime-local" value={toDate} onChange={handleDateChange(setToDate)} disabled={autoRefresh} />
            </Form.Group>
          </div>
          <div className="col-auto">
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Dropdown autoClose="outside">
                <Dropdown.Toggle variant="outline-secondary" className="d-block">
                  {selectedTypes.length ? `${selectedTypes.length} seleccionado(s)` : 'Todos'}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {typeOptions.length === 0 && (
                    <Dropdown.ItemText className="text-muted">Sin tipos</Dropdown.ItemText>
                  )}
                  {typeOptions.map((type) => (
                    <Dropdown.ItemText key={type}>
                      <Form.Check
                        type="checkbox"
                        id={`type-${type}`}
                        label={type}
                        checked={selectedTypes.includes(type)}
                        onChange={() => handleTypeToggle(type)}
                      />
                    </Dropdown.ItemText>
                  ))}
                  {selectedTypes.length > 0 && (
                    <>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={() => { setPage(1); setSelectedTypes([]); }}>
                        Limpiar
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          </div>
          <div className="col-auto">
            <Form.Group>
              <Form.Label>Recibido contiene</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar en recibido…"
                value={received}
                onChange={(e) => setReceived(e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-auto">
            <Form.Group>
              <Form.Label>Enviado contiene</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar en enviado…"
                value={sent}
                onChange={(e) => setSent(e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-auto">
            <Form.Check
              type="switch"
              id="auto-refresh"
              label="Auto refrescar (5s)"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
          </div>
          <div className="col-auto">
            <button type="button" className="btn btn-outline-secondary" onClick={handleClearAll}>
              Limpiar todo
            </button>
          </div>
        </div>

        {(!data || updating) ? <Spinner /> : (
          <>
            <table className="table table-hover table-striped">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Máquina</th>
                  <th>Tipo</th>
                  <th>Recibido</th>
                  <th>URL enviada</th>
                  <th>Enviado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <Rows data={data} onRowClick={(item) => navigate('/logs/' + item.id)} onCopy={handleCopy} />
              </tbody>
            </table>

            {total > 0 && (
              <div className="d-flex justify-content-end">
                <Paginator currentPage={page} totalPages={total} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Logs;
